import { CircleHelpIcon, HeartHandshakeIcon } from "lucide-react";
import React from "react";

import { cn } from "~/lib/cn";
import { DREAM_MATCH_SCORE_TOOLTIP } from "~/lib/coach-status";

import { Badge } from "./badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

interface ScoreBadgeProps {
  score: number;
  isBlurred?: boolean;
  onClick?: () => void;
  size?: "sm" | "default" | "lg";
  showTooltip?: boolean;
}

/** Match-score badge that can be blurred for visitors who have not signed up. */
export const ScoreBadge = ({
  score,
  isBlurred = false,
  onClick,
  size = "default",
  showTooltip = true,
}: ScoreBadgeProps) => {
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div className="absolute bottom-0 flex w-full justify-center">
      <Badge
        className={cn(
          "absolute -bottom-2 mx-auto",
          onClick && "cursor-pointer",
        )}
        variant="overlay"
        size={size}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : -1}
        aria-label={onClick ? "See how to view your match score" : undefined}
      >
        <HeartHandshakeIcon className="size-[1em]" />
        <span className={cn(isBlurred && "opacity-80 blur-[2px]")}>
          {score}
        </span>
        {showTooltip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <CircleHelpIcon className="size-[1em] cursor-help text-white/70 hover:text-white" />
            </TooltipTrigger>
            <TooltipContent>{DREAM_MATCH_SCORE_TOOLTIP}</TooltipContent>
          </Tooltip>
        )}
      </Badge>
    </div>
  );
};
